import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "src/components/buttons/shadcn-button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/components/form/form";
import { Input } from "src/components/input/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/input/Select";
import { TimePicker } from "src/components/ui/time-picker";
import {
  AddAttendanceSchema,
  AttendanceForm,
} from "src/schemas/add-attendance";
import { useSearchEmployeeByCode } from "./hooks/useSearchEmployeeByCode";
import { useAttendanceMutation } from "./hooks/useAttendanceMutation";
import { useDebounce } from "use-debounce";

const AddAttendanceForm = () => {
  const form = useForm<AttendanceForm>({
    resolver: zodResolver(AddAttendanceSchema),
    defaultValues: {
      employee_code: "",
      checkin_time: "",
      attendance_status: "",
      date: "",
    },
  });

  const employeeCode = form.watch("employee_code");
  const [debouncedEmployeecode] = useDebounce(employeeCode, 700);

  const { data: employee, isLoading } = useSearchEmployeeByCode(
    debouncedEmployeecode
  );

  const status = form.watch("attendance_status");

  useEffect(() => {
    if (status === "ABSENT") {
      form.setValue("checkin_time", "");
    }

    if (status === "FULLDAY") {
      form.setValue("checkin_time", "");
    }

    if (status === "HALFDAY") {
      form.setValue("checkin_time", "");
    }

  }, [status, form]);

  const { mutate: addAttendance, isPending } = useAttendanceMutation();

  const onSubmit = (values: AttendanceForm) => {
    addAttendance(values);
  };

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4 text-center text-primary">
        Add Attendance
      </h2>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4 max-w-md mx-auto p-6 border rounded-2xl shadow-md bg-card"
        >
          <FormField
            control={form.control}
            name="employee_code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Employee Code</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., 25090001" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {(isLoading) && (
            <p className="text-sm text-muted-foreground">
              Loading employee...{" "}
              <span className="loading loading-ring loading-lg"></span>
            </p>
          )}

          {employee && employee.map((emp) => (
            <div className="space-y-1 text-sm p-2 rounded-xl bg-gray-100" key={emp.id}>
              <p className="capitalize font-medium">
                {emp?.full_name} <span className="mx-2">|</span>{" "}
                {emp?.employee_code}
              </p>
              <p className="capitalize text-xs">{emp?.designation}</p>
            </div>
          ))}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="attendance_status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {["FULLDAY", "HALFDAY", "ABSENT"].map((status) => (
                          <SelectItem value={status} key={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex justify-between gap-6">
            {(status === "FULLDAY" || status === "HALFDAY") && (
              <FormField
                control={form.control}
                name="checkin_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Check-in Time (24 hr)</FormLabel>
                    <FormControl>
                      <TimePicker
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isPending}>
            {isPending ? (
              <span className="loading loading-dots loading-md"></span>
            ) : (
              "Save Attendance"
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default AddAttendanceForm;
